import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Divider,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, Delete, ReceiptLong } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcInvoiceApi, type EpcInvoice } from '../../api/epcBilling.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'paid' ? 'success' : s === 'approved' ? 'info' : s === 'submitted' ? 'warning' : s === 'rejected' ? 'error' : 'default';

export function EpcInvoicesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-invoices', page, limit, pid], queryFn: () => epcInvoiceApi.list({ page, limit, projectId: pid }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-invoices'] });

  const columns: Column<EpcInvoice>[] = [
    { key: 'invoiceCode', label: 'Invoice', render: (r) => r.invoiceCode },
    { key: 'projectId', label: 'Project', render: (r) => `#${r.projectId}` },
    { key: 'grossAmount', label: 'Gross', align: 'right', render: (r) => inr(r.grossAmount) },
    { key: 'retentionAmount', label: 'Retention', align: 'right', render: (r) => `${inr(r.retentionAmount)} (${r.retentionPercent}%)` },
    { key: 'netAmount', label: 'Net', align: 'right', render: (r) => <b>{inr(r.netAmount)}</b> },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="EPC Client Invoices (RA Bills)" subtitle="Milestone/progress-based client billing with retention; approve then record payment."
        action={hasPermission('epc_invoice.create') && (
          <Button startIcon={<Add />} variant="contained" disabled={!pid} onClick={() => { setError(null); setCreateOpen(true); }}>New Invoice</Button>
        )} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><ReceiptLong fontSize="small" /></IconButton></Tooltip>} />

      <CreateDialog open={createOpen} projectId={pid} onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function CreateDialog({ open, projectId, onClose, onDone, onError, error }: {
  open: boolean; projectId?: number; onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [title, setTitle] = useState('');
  const [retentionPercent, setRetentionPercent] = useState('10');
  const [dueDate, setDueDate] = useState('');
  const mut = useMutation({
    mutationFn: () => epcInvoiceApi.create({ projectId: projectId!, title: title || undefined, retentionPercent: retentionPercent ? Number(retentionPercent) : undefined, dueDate: dueDate || undefined }),
    onSuccess: (inv) => { setTitle(''); setRetentionPercent('10'); setDueDate(''); onDone(inv.id); },
    onError: (e) => onError(extractApiError(e)),
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Invoice — Project #{projectId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Retention %" type="number" size="small" fullWidth value={retentionPercent} onChange={(e) => setRetentionPercent(e.target.value)} />
            <TextField label="Due date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Stack>
          <Typography variant="caption" color="text.secondary">Add line items after creating the invoice.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: inv } = useQuery({ queryKey: ['epc-invoice', id], queryFn: () => epcInvoiceApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-invoice', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  const [it, setIt] = useState({ description: '', unit: '', quantity: '', rate: '' });
  const addItem = useMutation({
    mutationFn: () => epcInvoiceApi.addItem(id, { description: it.description, unit: it.unit || undefined, quantity: Number(it.quantity), rate: Number(it.rate) }),
    onSuccess: () => { setIt({ description: '', unit: '', quantity: '', rate: '' }); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });

  if (!inv) return null;
  const isDraft = inv.status === 'draft';
  const canEdit = isDraft && hasPermission('epc_invoice.update');

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{inv.invoiceCode} <Chip size="small" sx={{ ml: 1 }} label={inv.status} color={statusColor(inv.status)} />
        <Typography variant="body2" color="text.secondary">Project #{inv.projectId} · Gross {inr(inv.grossAmount)} − Retention {inr(inv.retentionAmount)} = <b>Net {inr(inv.netAmount)}</b>{inv.status === 'paid' ? ` · Paid ${inr(inv.paidAmount)}${inv.paymentRef ? ` (${inv.paymentRef})` : ''}` : ''}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {inv.status === 'rejected' && inv.remarks && <Alert severity="warning" sx={{ mb: 1 }}>Rejected: {inv.remarks}</Alert>}
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell>Unit</TableCell>
            <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell>{canEdit && <TableCell />}
          </TableRow></TableHead>
          <TableBody>
            {(inv.items ?? []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.description}</TableCell><TableCell align="right">{row.quantity}</TableCell><TableCell>{row.unit ?? '—'}</TableCell>
                <TableCell align="right">{inr(row.rate)}</TableCell><TableCell align="right">{inr(row.amount)}</TableCell>
                {canEdit && <TableCell><IconButton size="small" color="error" onClick={() => act(() => epcInvoiceApi.removeItem(id, row.id), 'Item removed')}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>
            ))}
            {(inv.items ?? []).length === 0 && <TableRow><TableCell colSpan={canEdit ? 6 : 5}><Typography variant="body2" color="text.secondary">No line items yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
        {canEdit && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField size="small" label="Description" value={it.description} onChange={(e) => setIt({ ...it, description: e.target.value })} sx={{ minWidth: 200 }} />
              <TextField size="small" label="Qty" type="number" value={it.quantity} onChange={(e) => setIt({ ...it, quantity: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Unit" value={it.unit} onChange={(e) => setIt({ ...it, unit: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Rate" type="number" value={it.rate} onChange={(e) => setIt({ ...it, rate: e.target.value })} sx={{ width: 110 }} />
              <Button startIcon={<Add />} variant="outlined" disabled={!it.description || !it.quantity || !it.rate || addItem.isPending} onClick={() => addItem.mutate()}>Add</Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isDraft && hasPermission('epc_invoice.delete') && <Button color="error" onClick={() => act(() => epcInvoiceApi.remove(id).then(() => onClose()), 'Invoice deleted')}>Delete</Button>}
        {isDraft && hasPermission('epc_invoice.submit') && <Button variant="contained" onClick={() => act(() => epcInvoiceApi.submit(id), 'Submitted for approval')}>Submit</Button>}
        {inv.status === 'submitted' && hasPermission('epc_invoice.approve') && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcInvoiceApi.reject(id, r), 'Rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcInvoiceApi.approve(id), 'Approved')}>Approve</Button>
          </>
        )}
        {inv.status === 'approved' && hasPermission('epc_invoice.pay') && (
          <Button variant="contained" color="success" onClick={() => { const ref = window.prompt('Payment reference (optional)?') ?? undefined; act(() => epcInvoiceApi.pay(id, { paymentRef: ref }), 'Marked paid'); }}>Record Payment</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
